import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoanAmmount,
  setMonthlyPay,
  setIntrestRate,
  setMonthsToPayoff,
  setCreditErrors,
  resetCreditData,
  setTotalAmount,
} from "../redux/calculations/creditSlice";
import {
  resetChartData,
  updatedCreditChart,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const CreditCalc = () => {
  const dispatch = useDispatch();
  const { loanAmmount, intrestRate, monthlyPay, creditErrors } = useSelector(
    (state) => state.creditCalculator
  );
  const [calcDone, setCalcDone] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);

  const { textData } = useLocale();
  const { creditCalc } = textData;
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const [interestExceedsLimit, setInterestExceedsLimit] = useState(false);
  const INTEREST_LIMIT = 1000000;

  const formik = useFormik({
    initialValues: {
      loanAmmount: loanAmmount,
      intrestRate: intrestRate,
      monthlyPay: monthlyPay,
    },
    validationSchema: Yup.object({
      loanAmmount: Yup.number()
        .min(500, creditCalc.loanAmountMin)
        .max(500000, creditCalc.loanAmountMax)
        .required(creditCalc.loanAmountRequired),
      intrestRate: Yup.number()
        .min(1, creditCalc.interestRateMin)
        .max(10, creditCalc.interestRateMax)
        .required(creditCalc.interestRateRequired),
      monthlyPay: Yup.number()
        .min(500, creditCalc.monthlyPayMin)
        .max(100000, creditCalc.monthlyPayMax)
        .required(creditCalc.monthlyPayRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_CREDIT");
      }
      const principal = parseFloat(values.loanAmmount);
      const annualInterestRate = parseFloat(values.intrestRate);
      const monthlyPayment = parseFloat(values.monthlyPay);
      if (monthlyPayment >= principal) {
        dispatch(setCreditErrors(textData.creditCalc.paymentLimit));
        setTimeout(() => {
          dispatch(setCreditErrors(null));
        }, 3000);
        return;
      }

      const estimatedInterest = estimateTotalInterest(
        principal,
        annualInterestRate,
        monthlyPayment
      );
      if (estimatedInterest > INTEREST_LIMIT) {
        setInterestExceedsLimit(true);

        dispatch(setCreditErrors(textData.creditCalc.paymentLimit));
        setTimeout(() => {
          dispatch(setCreditErrors(null));
          setInterestExceedsLimit(false);
        }, 3000);
        return;
      }

      const monthlyInterestRate = annualInterestRate / 1200;
      const monthlyInterest = principal * monthlyInterestRate;
      setInterestExceedsLimit(false);
      dispatch(setCreditErrors(null));

      let balance = principal;
      let totalInterest = 0;
      let months = 0;
      let totalPayment = 0;

      while (balance > 0) {
        const interest = (balance * annualInterestRate) / 100;
        totalInterest += interest;

        const principalPayment = Math.min(monthlyPayment - interest, balance);
        balance -= principalPayment;

        months++;

        if (months > 600) {
          break;
        }

        totalPayment = principal + totalInterest;

        if (totalInterest > INTEREST_LIMIT) {
          setInterestExceedsLimit(true);
          dispatch(setCreditErrors(textData.limitError));

          setTimeout(() => {
            dispatch(setCreditErrors(null));
            setInterestExceedsLimit(false);
          }, 3000); // 3 seconds

          return;
        } else {
          dispatch(setCreditErrors(null));
          setInterestExceedsLimit(false);
        }
      }

      dispatch(setMonthsToPayoff(months));
      dispatch(setLoanAmmount(principal));
      dispatch(setMonthlyPay(monthlyPayment));
      dispatch(setIntrestRate(annualInterestRate));
      dispatch(
        updatedCreditChart({
          loanAmount: principal,
          totalInterest: totalInterest,
          totalPayment: totalPayment.toFixed(2),
          monthsToPayoff: months,
        })
      );

      saveToHistory({
        tabName: "creditCardPayoffCalc",
        totalInvestment: principal,
        monthlyPayment: monthlyPayment,
        annualInterestRate: annualInterestRate,
        monthlyInterestRate: monthlyInterestRate,
        monthlyInterest: monthlyInterest,
        monthlyInterest: monthlyInterest,
        yearlyReturn: totalInterest,
        interestRate: values.intrestRate,
        tenure: months,
        maturityAmount: totalPayment,
        calculationType: "Credit",
      });
      dispatch(setTotalAmount(totalPayment));
      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });

  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetCreditData());
    };
  }, [dispatch]);
  const estimateTotalInterest = (principal, interestRate, monthlyPayment) => {
    const monthlyRate = interestRate / 1200;

    let estimatedMonths = 0;
    if (monthlyRate > 0) {
      estimatedMonths = Math.ceil(
        -Math.log(1 - (principal * monthlyRate) / monthlyPayment) /
        Math.log(1 + monthlyRate)
      );
    } else {
      estimatedMonths = Math.ceil(principal / monthlyPayment);
    }

    estimatedMonths = Math.min(estimatedMonths, 600);

    const estimatedInterest = monthlyPayment * estimatedMonths - principal;
    return Math.max(0, estimatedInterest);
  };

  useEffect(() => {
    const checkInterestLimit = () => {
      const principal = parseFloat(formik.values.loanAmmount) || 0;
      const rate = parseFloat(formik.values.intrestRate) || 0;
      const payment = parseFloat(formik.values.monthlyPay) || 0;

      if (principal > 0 && rate > 0 && payment > 0) {
        const estimatedInterest = estimateTotalInterest(
          principal,
          rate,
          payment
        );
        setInterestExceedsLimit(estimatedInterest > INTEREST_LIMIT);

        // if (estimatedInterest > INTEREST_LIMIT) {
        //   dispatch(setCreditErrors(textData.creditCalc.paymentLimit));
        //   setTimeout(() => {
        //     dispatch(setCreditErrors(null));
        //   }, 3000);
        // } else {
        //   dispatch(setCreditErrors(null));
        // }
      }
    };

    checkInterestLimit();
  }, [
    formik.values.loanAmmount,
    formik.values.intrestRate,
    formik.values.monthlyPay,
  ]);

  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];
      const newHistoryRecord = {
        tabName: calculationData.tabName,
        monthlyPayment: calculationData.monthlyPayment,
        annualInterestRate: calculationData.annualInterestRate,
        monthlyInterestRate: calculationData.monthlyInterestRate,
        monthlyInterest: calculationData.monthlyInterest,
        totalInvestment: calculationData.totalInvestment,
        yearlyReturn: calculationData.totalInterest,
        interestRate: calculationData.interestRate,
        tenure: calculationData.tenure,
        maturityAmount: calculationData.maturityAmount,
        calculationType: calculationData.calculationType,
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.totalInvestment === newHistoryRecord.totalInvestment &&
          record.interestRate === newHistoryRecord.interestRate &&
          record.tenure === newHistoryRecord.tenure &&
          record.maturityAmount === newHistoryRecord.maturityAmount
        );
      });

      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) { }
  };

  const handleInvestmentChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, "");
    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    ); // Convert to a number

    if (rawValue.length > 7) return;
    if (rawValue.match(/^[.,]+$/)) {
      return;
    }
    if (rawValue === "") {
      formik.setFieldValue("loanAmmount", "");
      dispatch(setLoanAmmount(""));
      return;
    }

    formik.setFieldValue("loanAmmount", numericValue);

    dispatch(setLoanAmmount(numericValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };

  const sliderTrackRef = useRef(null);
  const interestSliderRef = useRef(null);
  const monthlyPaySliderRef = useRef(null);

  const handleMonthlyPaySliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setMonthlyPay(value));
    formik.setFieldValue("monthlyPay", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleIntrestRatetSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setIntrestRate(value));
    formik.setFieldValue("intrestRate", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleLoanAmountSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setLoanAmmount(value));
    formik.setFieldValue("loanAmmount", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleMonthlyPayChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, "");
    if (rawValue.length > 6) return;
    if (rawValue === "") {
      formik.setFieldValue("monthlyPay", "");
      dispatch(setMonthlyPay(""));
      return;
    }
    if (rawValue.match(/^[.,]+$/)) {
      return;
    }
    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    ); // Convert to a number

    formik.setFieldValue("monthlyPay", numericValue);
    dispatch(setMonthlyPay(numericValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("intrestRate", rawValue);
      dispatch(setIntrestRate(""));

      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);
    formik.setFieldValue("intrestRate", normalizedValue);
    dispatch(setIntrestRate(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  useEffect(() => {
    formik.setValues({
      loanAmmount: loanAmmount,
      intrestRate: intrestRate,
      monthlyPay: monthlyPay,
    });
  }, [loanAmmount, intrestRate, monthlyPay]);

  useEffect(() => {
    dispatch(setLoanAmmount(formik.values.loanAmmount));
    dispatch(setMonthlyPay(formik.values.monthlyPay));
    dispatch(setIntrestRate(formik.values.intrestRate));
  }, [formik.values, dispatch]);
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.creditContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.creditContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.creditContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.creditContent.howItWorksDescription}
      </p>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.creditContent.howItWorksDescription2}
      </p>
      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        {textData.creditContent.interestFormulaLabel}
      </p>
      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.creditContent.inputsRequiredTitle}
      </h2>
      <table className="border-collapse mt-4 w-full">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-3 border border-gray-light w-1/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.creditContent.inputsTable.symbolColumn}
            </th>
            <th className="p-3 border border-gray-light w-2/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.creditContent.inputsTable.descriptionColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.creditContent.inputsTable.balance}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.creditContent.inputsTable.OutstandingBalance}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.creditContent.inputsTable.intrest}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.creditContent.inputsTable.MonthlyInterest}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.creditContent.inputsTable.monthlyPay}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.creditContent.inputsTable.MonthlyPayment}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.creditContent.resultsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.creditContent.resultsList[0]}</li>
        <li>{textData.creditContent.resultsList[1]}</li>
        <li>{textData.creditContent.resultsList[2]}</li>
        <li>{textData.creditContent.resultsList[3]}</li>
        <li>{textData.creditContent.resultsList[4]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.creditContent.useTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.creditContent.useList[0]}</li>
        <li>{textData.creditContent.useList[1]}</li>
        <li>{textData.creditContent.useList[2]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.creditContent.strategiesTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.creditContent.debt}</strong>{" "}
          {textData.creditContent.strategiesList[0]}
        </li>
        <li>
          <strong>{textData.creditContent.avalance}</strong>{" "}
          {textData.creditContent.strategiesList[1]}
        </li>
        <li>
          <strong>{textData.creditContent.console}</strong>{" "}
          {textData.creditContent.strategiesList[2]}
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.creditContent.importanceTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.creditContent.importanceList[0]}</li>
        <li>{textData.creditContent.importanceList[1]}</li>{" "}
        <li>{textData.creditContent.importanceList[2]}</li>
        <li>{textData.creditContent.importanceList[3]}</li>{" "}
      </ul>
    </div>
  );

  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };

  const getPlaceholderPayValue = (language) => {
    const baseValue = 5000;
    return new Intl.NumberFormat(language).format(baseValue);
  };

  return (
    <>
      <Helmet>
        <title>Credit Card Payoff Calculator | Compare Debt Payoff Loans</title>
        <meta
          name="description"
          content="Credit Card Payoff Calculator lets you compare debt payoff loans, plan smarter repayments, save more on interest, and reach your debt-free goals easily."
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/credit-card-payoff-calculator"
        />

        <meta
          property="og:title"
          content="Credit Card Payoff Calculator | Compare Debt Payoff Loans"
        />
        <meta
          property="og:description"
          content="Credit Card Payoff Calculator lets you compare debt payoff loans, plan smarter repayments, save more on interest, and reach your debt-free goals easily."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/credit-card-payoff-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Credit Card Payoff Calculator Online Calculator - GrowLifeFund"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>
      <CalculatorLayout
        // generateData={result}
        title={
          <>
            {" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.creditCalc.titleMain}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.creditCalc.titleSuffix}
            </span>
          </>
        }
        description={textData.creditCalc.description}
        descriptionContent={descriptionContent}
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        setCalcDone={setCalcDone}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.creditCalc.loanAmountLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={sliderTrackRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={500}
                    max={500000}
                    step={500}
                    value={loanAmmount}
                    onChange={handleLoanAmountSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((loanAmmount - 500) / (500000 - 500)) * 100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="loanAmmount"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.loanAmmount)}
                    onChange={handleInvestmentChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.loanAmmount && formik.errors.loanAmmount
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]    ${formik.touched.loanAmmount && formik.errors.loanAmmount
                        ? "border-danger"
                        : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.loanAmmount && formik.errors.loanAmmount && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.loanAmmount}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.creditCalc.interestRateLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={interestSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={1}
                    max={10}
                    step={0.1}
                    value={parseFloat(intrestRate)}
                    onChange={handleIntrestRatetSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((intrestRate - 1) / (10 - 1)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="intrestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("1.8")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.intrestRate
                    )}
                    onChange={handleInterestRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]    ${formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-danger"
                        : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.intrestRate && formik.errors.intrestRate && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.intrestRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.creditCalc.monthlyPayLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={monthlyPaySliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={500}
                    max={100000}
                    step={1}
                    value={monthlyPay}
                    onChange={handleMonthlyPaySliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((monthlyPay - 500) / 100000) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="monthlyPay"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.monthlyPay)}
                    onChange={handleMonthlyPayChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderPayValue(i18n.language)}
                    borderColor={
                      formik.touched.monthlyPay && formik.errors.monthlyPay
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]    ${formik.touched.monthlyPay && formik.errors.monthlyPay
                        ? "border-danger"
                        : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.monthlyPay && formik.errors.monthlyPay && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.monthlyPay}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px] relative">
              <Button
                type="submit"
                className="w-full mb-2"
                // disabled={interestExceedsLimit}
                style={
                  interestExceedsLimit
                    ? { opacity: 0.6, cursor: "not-allowed" }
                    : {}
                }
              >
                {textData.calculate}
              </Button>
              {creditErrors && (
                <div className=" absolute bottom-[-50] z-50 w-full text-red-500 mb-4 text-center px-2 py-2 sm:py-4  rounded-md text-black bg-gray-light border-none text-sm">
                  {creditErrors}
                </div>
              )}
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default CreditCalc;
